import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, IdCard } from 'lucide-react';
import { fetchDbUser } from '../../api/dbUserLookup';
import { isForbiddenError } from '../../utils/apiErrors';
import { parseVerifiedDocument } from '../../utils/userAdmin';
import { useDisableUser, useEnableUser } from '../../hooks/useSAMUsers';
import { useNationalities, resolveNationality, formatNationality } from '../../hooks/useNationalities';
import { S3Image } from '../../components/S3Image';
import { ResetPasswordButton } from '../../components/ResetPasswordButton';
import type { SAMUser } from '../../types/role.types';
import { useT, type MessageKey } from '../../i18n/admin';
import { useFdCan } from '../../hooks/useFdCan';

/**
 * SAM-198 — one team member of the business, opened from the Team tab (no split view).
 * Cognito data comes from the team list row; the ID document and general data come
 * from the SAM DB user row (GET /user/{user_id}, SAM-801), the same row the Add member flow
 * writes after creating the login (AddBusinessUserSheet).
 */

interface DbUser {
  id?: number;
  name?: string | null;
  last_name?: string | null;
  email?: string | null;
  tel?: string | null;
  photo_url?: string | null;
  idtype?: string | null;
  idnum?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  code?: string | null;
  /** SAM-629: { type, verified_at, … } once reception or the OCR verified the document; null otherwise. */
  verified_document?: unknown;
}

// SAM-1009: idtype is stored as 'documentId' (camelCase) as well as 'document_id' / 'cedula', so the lookup
// ignores case, spaces and underscores.
const ID_TYPES: Record<string, MessageKey> = {
  documentid: 'team.idType.documentId', cedula: 'team.idType.documentId', passport: 'team.idType.passport',
  driverlicense: 'team.idType.driverLicense', driverslicense: 'team.idType.driverLicense',
};
export const idTypeKey = (raw: string): MessageKey | undefined => ID_TYPES[raw.toLowerCase().replace(/[\s_-]+/g, '')];

// SAM-801: by the numeric user_id of the team list (#646); a confined caller may not read another user by cognitoid
// (#629). A 403 is not retried: the page then shows the Cognito data only.
const useDbUser = (member: SAMUser) =>
  useQuery<DbUser | null>({
    queryKey: ['db-user', member.user_id ?? null, member.sub || member.username],
    queryFn: () => fetchDbUser<DbUser>({ user_id: member.user_id, sub: member.sub || member.username }),
    enabled: typeof member.user_id === 'number' || !!(member.sub || member.username),
    retry: (count, error) => !isForbiddenError(error) && count < 2,
  });

// SAM-1075: a label over a value (Identity and Document) ...
const Field: React.FC<{ k: string; v?: React.ReactNode }> = ({ k, v }) => (
  <div className="min-w-0">
    <div className="text-[12px] text-[var(--ink-500)] mb-0.5">{k}</div>
    <div className="text-sm font-medium text-[var(--ink-900)] break-words">{v || '—'}</div>
  </div>
);

// ... and a label beside its value (Contact and access), a line between rows.
const Row: React.FC<{ k: string; v?: React.ReactNode }> = ({ k, v }) => (
  <div className="flex justify-between gap-3 text-sm py-[11px] first:pt-0 border-t border-solid border-[var(--line-subtle)] first:border-t-0">
    <span className="text-[var(--ink-500)]">{k}</span>
    <span className="font-medium text-right break-words min-w-0">{v || '—'}</span>
  </div>
);

const Pill: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={`text-[11px] px-[9px] py-0.5 rounded-[10px] font-medium inline-flex items-center gap-[5px] ${className}`}>{children}</span>
);

export const BusinessTeamMember: React.FC<{
  member: SAMUser;
  businessName?: string;
  onBack: () => void;
}> = ({ member, businessName, onBack }) => {
  // The tenant Front Desk sees the team but does not manage it (specs/front-desk-tenant-permisos.md).
  const canManage = useFdCan('business', 'edit');
  const { t, fmt } = useT();
  const formatDate = (iso?: string | null) => (iso ? fmt.date(iso) : '—');
  const { data: dbUser, isLoading, error: dbError } = useDbUser(member);
  // 403 on the DB row: show what Cognito knows and leave out the document fields this caller may not read.
  const dbForbidden = isForbiddenError(dbError);
  const { data: countries } = useNationalities();
  const nationality = resolveNationality(dbUser?.nationality, countries);
  const { disableUser, isDisabling } = useDisableUser();
  const { enableUser, isEnabling } = useEnableUser();
  const [confirming, setConfirming] = useState(false);
  // `member` is the list row it was opened from; keep its state in step after an action.
  const [enabled, setEnabled] = useState(member.enabled);

  const fullName = [member.name, member.family_name].filter(Boolean).join(' ') || member.email || member.username;
  const initials = ((member.name?.[0] || '') + (member.family_name?.[0] || '')).toUpperCase() || '?';
  const roles = (member.groups || [])
    .filter((g) => g.startsWith('business'))
    .map((g) => (g === 'business_admin' ? t('team.role.admin') : g === 'business_invitation' ? t('team.role.frontDesk') : g.replace('business_', '').replace(/_/g, ' ')));
  const status = !enabled ? t('common.status.inactive') : member.status === 'FORCE_CHANGE_PASSWORD' ? t('team.status.invited') : t('common.status.active');
  const hasId = !!(dbUser?.idnum || dbUser?.idtype);

  const verifiedAt = parseVerifiedDocument(dbUser?.verified_document)?.verified_at;
  const nationalityValue = nationality ? (
    <span className="inline-flex items-center gap-2">
      {/* country_flag is an image URL: draw it, never print it (SAM-1009). */}
      {nationality.country_flag && <img src={nationality.country_flag} alt="" className="h-3.5 w-5 rounded-[2px] object-cover" />}
      {nationality.country}
    </span>
  ) : dbUser?.nationality ? formatNationality(dbUser.nationality, countries) : undefined;
  const idTypeValue = dbUser?.idtype ? (idTypeKey(dbUser.idtype) ? t(idTypeKey(dbUser.idtype)!) : dbUser.idtype) : undefined;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('team.backToTeam')}
          className="w-10 h-10 shrink-0 rounded-[10px] border border-solid border-[var(--line)] bg-white flex items-center justify-center cursor-pointer"
        >
          <ArrowLeft size={17} />
        </button>
        <span className="w-16 h-16 shrink-0 rounded-full bg-[var(--line-subtle)] text-[var(--ink-700)] flex items-center justify-center text-[22px] font-semibold overflow-hidden">
          {dbUser?.photo_url ? (
            <S3Image imageKey={dbUser.photo_url} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : initials}
        </span>
        <div className="flex flex-col gap-2 grow min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="m-0 text-xl sm:text-2xl font-semibold text-[var(--ink-900)] break-words">{fullName}</h1>
            {!dbForbidden && !isLoading && (
              <span
                role="img"
                aria-label={hasId ? t('team.idOnFile') : t('team.idMissing')}
                title={`${t('team.document')}: ${hasId ? t('team.idOnFile') : t('team.idMissing')}`}
                className="relative w-8 h-8 shrink-0 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[var(--ink-700)] inline-flex items-center justify-center"
              >
                <IdCard size={17} />
                <span className={`absolute -top-[3px] -right-[3px] w-[9px] h-[9px] rounded-full border-2 border-solid border-white ${hasId ? 'bg-[var(--ok-dot,#0e8345)]' : 'bg-[#F59E0B]'}`} />
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-[11px] font-medium uppercase tracking-[.05em] text-[var(--ink-400)]">{t('team.field.role')}</span>
            <Pill className="bg-[var(--warn-bg)] text-[var(--warn-fg)]">{roles.join(', ') || t('team.noRole')}</Pill>
            <Pill className={enabled ? 'bg-[var(--ok-bg)] text-[var(--ok-fg)]' : 'bg-[var(--line-subtle)] text-[var(--ink-500)]'}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />{status}
            </Pill>
            {businessName && (
              <>
                <span className="text-[var(--line)]" aria-hidden>·</span>
                <Pill className="bg-[var(--line-subtle)] text-[var(--ink-700)]">{businessName}</Pill>
              </>
            )}
          </div>
        </div>
        {canManage && <div className="flex flex-wrap gap-2">
          <ResetPasswordButton
            displayName={fullName}
            target={{
              username: member.username,
              sub: member.sub,
              email: member.email,
              groups: member.groups,
              businessId: member.custom_attributes?.business_id ?? null,
              enabled,
              status: member.status,
            }}
          />
          {enabled ? (
            confirming ? (
              <>
                <button type="button" onClick={() => setConfirming(false)} className="h-10 px-3.5 rounded-[10px] border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer">
                  {t('team.keepActive')}
                </button>
                <button
                  type="button"
                  disabled={isDisabling}
                  onClick={() => disableUser(member.username, { onSuccess: () => { setConfirming(false); setEnabled(false); } })}
                  className="h-10 px-3.5 rounded-[10px] border-0 bg-[var(--bad-fg)] text-white text-sm font-semibold cursor-pointer disabled:opacity-60"
                >
                  {isDisabling ? t('team.deactivating') : t('team.confirmDeactivate')}
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="h-10 px-3.5 rounded-[10px] border border-solid border-[var(--bad-fg)] bg-white text-[var(--bad-fg)] text-sm font-semibold cursor-pointer">
                {t('team.deactivate')}
              </button>
            )
          ) : (
            <button
              type="button"
              disabled={isEnabling}
              onClick={() => enableUser(member.username, { onSuccess: () => setEnabled(true) })}
              className="h-10 px-3.5 rounded-[10px] border border-solid border-[var(--line)] bg-white text-sm font-semibold cursor-pointer disabled:opacity-60"
            >
              {isEnabling ? t('team.activating') : t('team.activate')}
            </button>
          )}
        </div>}
      </div>

      {/* SAM-1075: two cards of the same weight. "User Logs" (sign-ins, passes, access changes) waits for the backend to record those events. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        <div className="rounded-xl border border-solid border-[var(--line)] bg-white p-6 flex flex-col gap-6">
          <section aria-labelledby="tm-identity">
            <h2 id="tm-identity" className="m-0 mb-3.5 text-[15px] font-semibold">{t('team.identity')}</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-[18px]">
              <Field k={t('team.field.firstName')} v={dbUser?.name || member.name} />
              <Field k={t('team.field.lastName')} v={dbUser?.last_name || member.family_name} />
              {!dbForbidden && <Field k={t('team.field.dateOfBirth')} v={dbUser?.birthdate ? formatDate(dbUser.birthdate) : undefined} />}
              {!dbForbidden && <Field k={t('team.field.nationality')} v={nationalityValue} />}
            </div>
          </section>
          {!dbForbidden && (
            <section aria-labelledby="tm-document" className="border-t border-solid border-[var(--line-subtle)] pt-5">
              <h2 id="tm-document" className="m-0 mb-3.5 text-[15px] font-semibold">{t('team.document')}</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-[18px]">
                <Field k={t('team.field.idType')} v={idTypeValue} />
                <Field k={t('team.field.idNumber')} v={dbUser?.idnum} />
                <Field k={t('team.field.verified')} v={verifiedAt ? formatDate(verifiedAt) : undefined} />
              </div>
            </section>
          )}
        </div>

        <section aria-labelledby="tm-access" className="rounded-xl border border-solid border-[var(--line)] bg-white p-6 flex flex-col">
          <h2 id="tm-access" className="m-0 mb-3.5 text-[15px] font-semibold">{t('team.contactAccess')}</h2>
          <Row k={t('team.field.phone')} v={dbUser?.tel || member.phone_number} />
          <Row k={t('team.field.email')} v={member.email || dbUser?.email || dbUser?.code} />
          <Row k={t('team.field.role')} v={roles.join(', ')} />
          <Row k={t('team.field.business')} v={businessName} />
          <Row k={t('team.field.status')} v={status} />
          <Row k={t('team.field.added')} v={formatDate(member.created)} />
          <Row k={t('team.field.lastChange')} v={formatDate(member.last_modified)} />
        </section>
      </div>
    </div>
  );
};
